import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import {
  AssociationCardComponent,
  BrandingService,
  CardAction,
  CardData,
  COMPANY_PROFILE_CARD_DATA_TOKEN,
  CrmPlatformApiService,
  PageAnalyticsInjectionToken,
  pageAnalyticsInjectionTokenGenerator,
} from '@galaxy/crm/static';
import { PartnerDomainService } from '@galaxy/partner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { NamespaceResolutionService } from '../../namespace-resolution.service';
import { CompanyAccountService } from '../../snapshot/company-account.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-associated-account',
  imports: [
    CommonModule,
    MatButtonModule,
    MatExpansionModule,
    MatCardModule,
    MatIconModule,
    GalaxyBadgeModule,
    MatMenuModule,
    GalaxyLoadingSpinnerModule,
    TranslateModule,
    AssociationCardComponent,
  ],
  providers: [
    {
      provide: PageAnalyticsInjectionToken,
      useFactory: pageAnalyticsInjectionTokenGenerator('Company'),
    },
    BrandingService,
    CrmPlatformApiService,
    AccountGroupService,
    TranslateService,
  ],
  templateUrl: './associated-account.component.html',
  styleUrls: ['./associated-account.component.scss'],
})
export class AssociatedAccountComponent {
  data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);

  private companyAccountService = inject(CompanyAccountService);
  private accountGroupService = inject(AccountGroupService);
  private translateService = inject(TranslateService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly namespaceResolutionService = inject(NamespaceResolutionService);
  private readonly partnerDomainService = inject(PartnerDomainService);

  private readonly showPanel$$ = new BehaviorSubject<boolean>(false);
  protected showPanel$ = this.showPanel$$.asObservable();
  isAccountGroupLoading = toSignal(this.companyAccountService.createAccountGroupLoading$(this.data.company_id));
  accountGroupId$: Observable<string> = this.companyAccountService.accountGroupId$;
  private accountGroup$: Observable<AccountGroup | null> = this.accountGroupId$.pipe(
    distinctUntilChanged(),
    switchMap((accountGroupId) => {
      if (!accountGroupId) {
        return of(null);
      }

      return this.accountGroupService.get(
        accountGroupId,
        new ProjectionFilter({
          napData: true,
        }),
      );
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 403) {
        this.showPanel$$.next(true);
      }
      return throwError(() => error);
    }),
    shareReplay(1),
  );
  accountGroupInfo$: Observable<CardData> = this.accountGroup$.pipe(
    filter((accountGroup) => accountGroup !== null),
    map((accountGroup) => {
      const accountGroupId = accountGroup?.accountGroupId ?? '';
      return {
        type: 'account',
        accountGroupCompanyId: accountGroupId,
        accountGroupCompanyName: accountGroup?.napData?.companyName ?? '',
        profileButtonUrl: '',
        hideAvatar: true,
      };
    }),
  );
  accountGroupActions$: Observable<CardAction[]> = this.namespaceResolutionService.namespace$.pipe(
    switchMap((partnerId) => this.partnerDomainService.getDomain(partnerId)),
    map((pccDomain) => {
      const companyId = this.data.company_id;
      return [
        {
          label: this.translateService.instant('ASSOCIATIONS.ACCOUNT.GO_TO_ACCOUNT'),
          callback: () => {
            window.open(`${pccDomain}/crm/company/profile/${companyId}`, '_blank');
          },
        },
      ];
    }),
  );

  constructor() {
    this.companyAccountService.setAccountGroupId(this.data.account_group_id ?? '');
    this.accountGroup$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.showPanel$$.next(true);
    });
  }

  createAccount() {
    if (this.data.account_group_id) {
      this.companyAccountService.setAccountGroupId(this.data.account_group_id);
      return;
    }
    const companyId = this.data.company_id;
    firstValueFrom(this.companyAccountService.fetchAccountGroupId$(companyId)).then((accountGroupId) => {
      if (accountGroupId) {
        this.companyAccountService.setAccountGroupId(accountGroupId);
      } else {
        this.snackbarService.openErrorSnack('CRM.ERROR.ACCOUNT_GROUP_CREATION_FAILED');
      }
    });
  }
}
